import AdminDashboard from "./AdminDashboard";
import { Typography, Chip, Alert } from "@mui/material";
import Component from "components/Component";
import TemplateValidationDialog from "components/common/TemplateValidationDialog";
import DashboardContent from "components/layout/DashboardContent";
import { TemplateRequestHistory } from "components/templateRequests";
import { useCallback, useState } from "react";
import {
  useListTemplateRequestsQuery,
  useUpdateTemplateRequestMutation,
} from "services/docGenService";

const AdminTemplateRequests = ({ companyId }) => {
  const {
    data: requestsData,
    isLoading,
    error: loadError,
    refetch: refetchRequests,
  } = useListTemplateRequestsQuery({
    includeAll: true,
    impersonateCompanyId: companyId,
  });

  const [updateTemplateRequest] = useUpdateTemplateRequestMutation();

  const requests = requestsData?.requests || [];

  // A status change the server refused because the completed template failed
  // validation. The report is shown and the admin may complete anyway with a
  // reason, which re-issues the same update with `force`.
  const [pendingValidation, setPendingValidation] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  const handleStatusChange = useCallback(
    async (requestId, { status, descriptionMessage }) => {
      const result = await updateTemplateRequest({
        requestId,
        status,
        descriptionMessage,
        impersonateCompanyId: companyId,
      });
      if (result?.error?.type === "TEMPLATE_VALIDATION_FAILED") {
        setOverrideReason("");
        setPendingValidation({
          requestId,
          status,
          report: result.error.body?.validation ?? null,
          message: result.error.message,
        });
        return;
      }
      refetchRequests();
    },
    [updateTemplateRequest, companyId, refetchRequests]
  );

  const handleCloseValidation = useCallback(() => {
    setPendingValidation(null);
    setOverrideReason("");
  }, []);

  const handleCompleteAnyway = useCallback(async () => {
    if (!pendingValidation) {
      return;
    }
    const { requestId, status } = pendingValidation;
    await updateTemplateRequest({
      requestId,
      status,
      descriptionMessage: overrideReason,
      force: true,
      impersonateCompanyId: companyId,
    });
    handleCloseValidation();
    refetchRequests();
  }, [
    pendingValidation,
    overrideReason,
    updateTemplateRequest,
    companyId,
    handleCloseValidation,
    refetchRequests,
  ]);

  return (
    <div className={"app"}>
      <AdminDashboard>
        <DashboardContent>
          <Component
            title={"Template Requests"}
            options={
              // An in-flight query has no requests yet, which is not the same
              // as having none, so the count waits for the numbers.
              isLoading ? null : (
                <Chip
                  label={`${requests.length} request${
                    requests.length !== 1 ? "s" : ""
                  }`}
                  size={"small"}
                  color={"primary"}
                  variant={"outlined"}
                />
              )
            }
          >
            <Alert severity={"info"} sx={{ mb: 2 }}>
              <Typography variant={"body2"}>
                {
                  "Review and manage template requests from users. Document previews load using the selected company's file context."
                }
              </Typography>
            </Alert>

            {/* No empty state of its own: `TemplateRequestHistory` carries
                one, guarded by the same loading check, so keeping a second
                here would swap the whole component out the moment the query
                settled on nothing — the layout jump the loading state exists
                to prevent, and a divergence from the other mount point. */}
            {loadError ? (
              <Alert severity={"error"} sx={{ mb: 2 }}>
                {"Failed to load template requests. Please try again."}
              </Alert>
            ) : (
              <TemplateRequestHistory
                submittedRequests={requests}
                isAdmin={true}
                isLoading={isLoading}
                currentUserEmail={"admin"}
                onSaveRequest={handleStatusChange}
                impersonateCompanyId={companyId}
              />
            )}
          </Component>
        </DashboardContent>
      </AdminDashboard>

      <TemplateValidationDialog
        open={pendingValidation != null}
        onClose={handleCloseValidation}
        reports={pendingValidation?.report ? [pendingValidation.report] : []}
        onProceed={handleCompleteAnyway}
        proceedLabel={"Complete anyway"}
        requireReason
        reason={overrideReason}
        onReasonChange={setOverrideReason}
        failureMessage={pendingValidation?.message ?? null}
      />
    </div>
  );
};

export default AdminTemplateRequests;
